import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, Teleport as _Teleport, openBlock as _openBlock, createBlock as _createBlock, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, unref as _unref, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "flex-grow bg-neutral-100" }
const _hoisted_2 = { class: "container mx-auto max-w-6xl" }
const _hoisted_3 = { class: "flex flex-col justify-between mt-8 mb-6 ml-6 sm:flex-row sm:items-center" }
const _hoisted_4 = { class: "text-3xl" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { class: "bg-white shadow p-6 pb-10 rounded-t" }
const _hoisted_8 = {
  key: 1,
  "aria-hidden": "true"
}
const _hoisted_9 = { class: "mb-3" }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { class: "font-semibold" }
const _hoisted_12 = { class: "mt-8 mb-2 font-semibold text-2xl" }
const _hoisted_13 = { class: "flex flex-col lg:flex-row" }
const _hoisted_14 = { class: "shrink max-w-xl" }
const _hoisted_15 = {
  class: "mt-3 w-[500px] max-w-full sm:px-6 lg:mt-0",
  autocomplete: "off"
}

import {
  computed, ref, watch,
} from 'vue';
import { useRouter } from 'vue-router';
import axios, { Canceler } from 'axios';
import { domainNameToDisplayName, domainNameToUrlPath, urlPathToDomainName } from '@/common/domainName';
import HeaderComponent from '@/components/layout/HeaderComponent.vue';
import FooterComponent from '@/components/layout/FooterComponent.vue';
import DomainSearch from '@/components/DomainSearch.vue';
import Alert from '@/components/micro/AlertComponent.vue';
import DnsRecordsLink from '@/components/micro/DnsRecordsLink.vue';
import { getRecordType } from '@/common/recordTypes';
import RawRecords from '@/components/micro/RawRecords.vue';
import DnsRecords from '@/components/records/DnsRecords.vue';
import SoaRecords from '@/components/records/SoaRecords.vue';
import IpDnsRecords from '@/components/records/IpDnsRecords.vue';
import TxtRecords from '@/components/records/TxtRecords.vue';
import NsRecords from '@/components/records/NsRecords.vue';
import MxRecords from '@/components/records/MxRecords.vue';
import MetaTags from '@/components/MetaTags.vue';
import SrvRecords from '@/components/records/SrvRecords.vue';
import CaaRecords from '@/components/records/CaaRecords.vue';
import PtrRecords from '@/components/records/PtrRecords.vue';
import TlsaRecords from '@/components/records/TlsaRecords.vue';
import SkeletonComponent from '@/components/micro/SkeletonComponent.vue';
import { ApiErrorType, toErrorType } from '@/model/ApiErrorAlert';
import ApiErrorAlert from '@/components/ApiErrorAlert.vue';
import SpfRecords from '@/components/records/SpfRecords.vue';
import {
  OtherRecordsResponse, QueryResult, emptyDnsServer, emptyResponse,
} from '@/model/Api';
import {
  Billboard, LargeLeaderboard, LargeRectangle, Leaderboard, MediumRectangle,
} from '@/common/adSizes';
import AdUnit from '@/components/ads/AdUnit.vue';
import { RecordTypeResults } from '@/common/adSlots';


export default /*@__PURE__*/_defineComponent({
  __name: 'RecordTypeLookupResults',
  props: {
  routerDomain: {
    type: String,
    required: true,
  },
  routerRecordType: {
    type: String,
    required: true,
  },
},
  setup(__props) {

const props = __props;

const router = useRouter();
const recordType = computed(() => getRecordType(props.routerRecordType));

const domain = ref('');
const domainPunycode = ref('');
const loading = ref(true);
const error = ref<ApiErrorType | null>(null);
const showSidebar = ref(window.innerWidth > 991);
const cancelToken = ref<Canceler | null>(null);
const queryResult = ref<QueryResult>({
  query: {
    server: emptyDnsServer,
    domain: domain.value,
    recordType: recordType.value.upper,
  },
  response: emptyResponse,
});

function domainToUrl(newDomain: string): string {
  if (recordType.value.upper === 'SPF') {
    return `/domains/${domainNameToUrlPath(newDomain)}/email/${recordType.value.lower}/`;
  }
  return `/domains/${domainNameToUrlPath(newDomain)}/dns-records/${recordType.value.lower}/`;
}

function redirectTo(newDomain: string): void {
  router.push(domainToUrl(newDomain));
}

function load() {
  loading.value = true;
  error.value = null;

  if (cancelToken.value) {
    cancelToken.value();
  }
  const axiosSource = axios.CancelToken.source();
  cancelToken.value = axiosSource.cancel;

  // Normally, we want to resolve the hostname of an IP address (which is what `/other-records` does).
  // But when looking up a PTR, we should show the result of querying the PTR records.
  const endpoint = recordType.value.upper === 'PTR' ? 'api/v1/ptr-records' : 'api/v1/other-records';

  axios
    .post<OtherRecordsResponse>(endpoint, {
      domain: domain.value,
      dnsServer: 'authoritative',
      recordType: recordType.value.cli,
    }, {
      cancelToken: axiosSource.token,
    })
    .then((response) => {
      if (props.routerDomain !== domainNameToUrlPath(response.data.punycodeDomain)) {
        router.replace({
          path: domainToUrl(response.data.punycodeDomain),
        });
      }
      domain.value = domainNameToDisplayName(response.data.unicodeDomain);
      domainPunycode.value = domainNameToDisplayName(response.data.punycodeDomain);
      queryResult.value = response.data.queryResult;
      loading.value = false;
    })
    .catch((e) => {
      if (!axios.isCancel(e)) {
        error.value = toErrorType(e.response.data.error);
      }
    });
}

watch(() => props.routerDomain, () => {
  const domainName = urlPathToDomainName(props.routerDomain);
  domain.value = domainName;
  domainPunycode.value = domainName;
  load();
}, {
  immediate: true,
});

window.addEventListener('resize', () => {
  showSidebar.value = window.innerWidth > 991;
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      _createVNode(MetaTags, {
        title: `DNS ${recordType.value.upper} records for ${domain.value}`,
        description: `These are all the ${recordType.value.plural} (${recordType.value.upper} records) for ${domain.value} according to the authoritative DNS server.`,
        url: `https://www.nslookup.io${domainToUrl(domainPunycode.value)}`
      }, null, 8, ["title", "description", "url"])
    ])),
    _createVNode(HeaderComponent, {
      domain: domain.value,
      "domain-search-to-url": domainToUrl,
      "search-button-text": ['soa', 'spf', 'cname'].includes(recordType.value.lower) ? `Find ${recordType.value.upper} record` : `Find ${recordType.value.upper} records`
    }, null, 8, ["domain", "search-button-text"]),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("main", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("h1", _hoisted_4, [
            _createTextVNode(_toDisplayString(recordType.value.upper) + " " + _toDisplayString(['soa', 'spf', 'cname'].includes(recordType.value.lower) ? 'record' : 'records') + " for ", 1),
            (domain.value === domainPunycode.value)
              ? (_openBlock(), _createElementBlock("b", _hoisted_5, _toDisplayString(domain.value), 1))
              : (_openBlock(), _createElementBlock("span", _hoisted_6, [
                  _createElementVNode("b", null, _toDisplayString(domainPunycode.value), 1),
                  _createTextVNode(" (" + _toDisplayString(domain.value) + ")", 1)
                ]))
          ]),
          _createVNode(DnsRecordsLink, {
            domain: domain.value,
            class: "shrink-0 hidden mr-6 sm:block xl:mr-0"
          }, {
            default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
              _createElementVNode("button", {
                type: "button",
                class: "inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              }, "All DNS records", -1)
            ]))]),
            _: 1
          }, 8, ["domain"])
        ]),
        _createElementVNode("div", _hoisted_7, [
          (error.value !== null)
            ? (_openBlock(), _createBlock(ApiErrorAlert, {
                key: 0,
                error: error.value,
                domain: domain.value,
                dnsserver: "authoritative"
              }, null, 8, ["error", "domain"]))
            : (loading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  _createVNode(SkeletonComponent, { class: "w-56 h-8 mb-6" }),
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(2, (n) => {
                    return _createElementVNode("div", { key: n }, [
                      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(2, (n) => {
                        return _createVNode(SkeletonComponent, {
                          key: n,
                          class: "inline-block w-1/2 h-4 pr-[10%] bg-clip-content"
                        })
                      }), 64))
                    ])
                  }), 64)),
                  _createVNode(SkeletonComponent, { class: "inline-block w-56 h-8 my-6" }),
                  _createVNode(SkeletonComponent, { class: "w-24 h-4 mb-2" }),
                  _createVNode(SkeletonComponent, { class: "w-1/2 h-4 mb-6" }),
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(3, (n) => {
                    return (_openBlock(), _createElementBlock(_Fragment, { key: n }, [
                      _createVNode(SkeletonComponent, { class: "w-24 h-4 mb-2 block" }),
                      _createVNode(SkeletonComponent, { class: "w-1/2 h-4 mb-2 block" }),
                      _createVNode(SkeletonComponent, { class: "w-1/2 h-4 mb-6" })
                    ], 64))
                  }), 64))
                ]))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                  _createElementVNode("p", _hoisted_9, "An authoritative DNS server (" + _toDisplayString(queryResult.value.query.server.domain) + ") responded with these DNS records when we queried it for the domain " + _toDisplayString(domain.value) + ".", 1),
                  (queryResult.value.response.answer.length === 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                        _createVNode(Alert, { type: "warning" }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", null, [
                              _createElementVNode("span", _hoisted_11, _toDisplayString(domain.value), 1),
                              _createTextVNode(" does not have any " + _toDisplayString(recordType.value.upper) + " records.", 1)
                            ])
                          ]),
                          _: 1
                        })
                      ]))
                    : (recordType.value.upper === 'A')
                      ? (_openBlock(), _createBlock(IpDnsRecords, {
                          key: 1,
                          class: "w-full",
                          records: queryResult.value,
                          "column-title": "IPv4 address",
                          "record-type": "A"
                        }, null, 8, ["records"]))
                      : (recordType.value.upper === 'AAAA')
                        ? (_openBlock(), _createBlock(IpDnsRecords, {
                            key: 2,
                            class: "w-full",
                            records: queryResult.value,
                            "column-title": "IPv6 address",
                            "record-type": "AAAA"
                          }, null, 8, ["records"]))
                        : (recordType.value.upper === 'CAA')
                          ? (_openBlock(), _createBlock(CaaRecords, {
                              key: 3,
                              class: "w-full",
                              records: queryResult.value,
                              domain: domain.value
                            }, null, 8, ["records", "domain"]))
                          : (recordType.value.upper === 'CNAME')
                            ? (_openBlock(), _createBlock(DnsRecords, {
                                key: 4,
                                class: "w-full",
                                records: queryResult.value,
                                "column-title": "Canonical name",
                                "record-type": "CNAME",
                                "is-domain": true
                              }, null, 8, ["records"]))
                            : (recordType.value.upper === 'TXT')
                              ? (_openBlock(), _createBlock(TxtRecords, {
                                  key: 5,
                                  class: "w-full",
                                  records: queryResult.value,
                                  domain: domain.value
                                }, null, 8, ["records", "domain"]))
                              : (recordType.value.upper === 'NS')
                                ? (_openBlock(), _createBlock(NsRecords, {
                                    key: 6,
                                    class: "w-full",
                                    records: queryResult.value,
                                    domain: domain.value
                                  }, null, 8, ["records", "domain"]))
                                : (recordType.value.upper === 'PTR')
                                  ? (_openBlock(), _createBlock(PtrRecords, {
                                      key: 7,
                                      class: "w-full",
                                      records: queryResult.value,
                                      domain: domain.value
                                    }, null, 8, ["records", "domain"]))
                                  : (recordType.value.upper === 'MX')
                                    ? (_openBlock(), _createBlock(MxRecords, {
                                        key: 8,
                                        class: "w-full",
                                        records: queryResult.value
                                      }, null, 8, ["records"]))
                                    : (recordType.value.upper === 'SOA')
                                      ? (_openBlock(), _createBlock(SoaRecords, {
                                          key: 9,
                                          class: "w-full",
                                          records: queryResult.value,
                                          domain: domain.value
                                        }, null, 8, ["records", "domain"]))
                                      : (recordType.value.upper === 'SRV')
                                        ? (_openBlock(), _createBlock(SrvRecords, {
                                            key: 10,
                                            class: "w-full",
                                            records: queryResult.value,
                                            domain: domain.value
                                          }, null, 8, ["records", "domain"]))
                                        : (recordType.value.upper === 'TLSA')
                                          ? (_openBlock(), _createBlock(TlsaRecords, {
                                              key: 11,
                                              class: "w-full",
                                              records: queryResult.value,
                                              domain: domain.value
                                            }, null, 8, ["records", "domain"]))
                                          : (recordType.value.upper === 'SPF')
                                            ? (_openBlock(), _createBlock(SpfRecords, {
                                                key: 12,
                                                class: "w-full",
                                                records: queryResult.value,
                                                domain: domain.value
                                              }, null, 8, ["records", "domain"]))
                                            : (_openBlock(), _createBlock(DnsRecords, {
                                                key: 13,
                                                class: "w-full",
                                                records: queryResult.value,
                                                "column-title": "Data",
                                                "record-type": recordType.value.upper
                                              }, null, 8, ["records", "record-type"])),
                  _createVNode(AdUnit, {
                    class: "mt-8 mx-auto",
                    config: _unref(RecordTypeResults)
                  }, null, 8, ["config"]),
                  _cache[1] || (_cache[1] = _createElementVNode("h2", { class: "mt-8 mb-2 font-semibold text-2xl" }, "Question and response", -1)),
                  _createVNode(RawRecords, { records: queryResult.value }, null, 8, ["records"])
                ], 64)),
          _createElementVNode("h2", _hoisted_12, "Find " + _toDisplayString(recordType.value.upper) + " records for another domain name", 1),
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("p", _hoisted_14, [
              _cache[2] || (_cache[2] = _createTextVNode("With ", -1)),
              _createVNode(_component_router_link, {
                to: `/${recordType.value.lower}-lookup/`,
                class: "text-blue-600 hover:text-blue-800 hover:underline"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(recordType.value.upper) + " lookup", 1)
                ]),
                _: 1
              }, 8, ["to"]),
              _createTextVNode(", you can find the " + _toDisplayString(recordType.value.upper) + " record for any domain name or subdomain. When you enter the domain in the input field below, it will show whether or not " + _toDisplayString(recordType.value.plural) + " are configured for that domain.", 1)
            ]),
            _createElementVNode("form", _hoisted_15, [
              _createVNode(DomainSearch, {
                "placeholder-text": recordType.value.upper === 'PTR' ? 'IP address or domain name' : 'Domain name',
                emphasize: "",
                buttonText: `Find ${recordType.value.upper} record`,
                prefill: domain.value,
                onSubmitted: redirectTo
              }, null, 8, ["placeholder-text", "buttonText", "prefill"])
            ])
          ])
        ])
      ])
    ]),
    _createVNode(FooterComponent)
  ], 64))
}
}

})